import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "../lib/ui";

type UnlockModalState = { channelId: string; spaceId: string; channelName?: string };

type UseChannelLocksArgs = {
  fq: (voidId: string, channelId: string) => string;
  token: string;
  currentVoidIdRef: React.MutableRefObject<string>;
  switchVoid: (voidId: string) => void;
  switchChannel: (channelId: string) => void;
};

export function useChannelLocks({
  fq,
  token,
  currentVoidIdRef,
  switchVoid,
  switchChannel,
}: UseChannelLocksArgs) {
  const [lockedChannelMap, setLockedChannelMap] = useState<Record<string, boolean>>({});
  const lockedChannelMapRef = useRef<Record<string, boolean>>({});
  useEffect(() => {
    lockedChannelMapRef.current = lockedChannelMap;
  }, [lockedChannelMap]);

  const pendingLockedChannelRef = useRef<{ channelId: string; spaceId: string } | null>(null);
  const pendingChannelSwitchRef = useRef<{ voidId: string; channelId: string } | null>(null);
  const [unlockModal, setUnlockModal] = useState<UnlockModalState | null>(null);
  const [unlockingChannel, setUnlockingChannel] = useState(false);
  const bypassUnlockedRef = useRef<string | null>(null);

  const isChannelLocked = useCallback(
    (spaceId: string, shortChannelId: string) => {
      if (!spaceId || !shortChannelId) return false;
      const fqid = fq(spaceId, shortChannelId);
      if (bypassUnlockedRef.current && bypassUnlockedRef.current === fqid) {
        bypassUnlockedRef.current = null;
        return false;
      }
      return !!lockedChannelMapRef.current[fqid];
    },
    [fq]
  );

  const maybePromptChannelUnlock = useCallback(
    (spaceId: string, shortChannelId: string, channelName?: string) => {
      if (!spaceId || !shortChannelId) return false;
      if (isChannelLocked(spaceId, shortChannelId)) {
        pendingLockedChannelRef.current = { channelId: shortChannelId, spaceId };
        setUnlockModal({ channelId: shortChannelId, spaceId, channelName });
        return true;
      }
      return false;
    },
    [isChannelLocked]
  );

  const closeUnlockModal = useCallback(() => {
    if (unlockingChannel) return;
    setUnlockModal(null);
    pendingLockedChannelRef.current = null;
  }, [unlockingChannel]);

  const handleUnlockChannel = useCallback(
    async (passcode: string) => {
      if (!unlockModal || unlockingChannel) return;
      const trimmed = passcode.trim();
      if (!trimmed) {
        toast("Enter a passcode to unlock this channel", "error");
        return;
      }
      setUnlockingChannel(true);
      try {
        await api.postAuth(
          "/channels/verify-passcode",
          { spaceId: unlockModal.spaceId, channelId: fq(unlockModal.spaceId, unlockModal.channelId), passcode: trimmed },
          token
        );
        setUnlockingChannel(false);
        setUnlockModal(null);
        const pending = pendingLockedChannelRef.current;
        pendingLockedChannelRef.current = null;
        if (pending) {
          bypassUnlockedRef.current = fq(pending.spaceId, pending.channelId);
          if (pending.spaceId && pending.spaceId !== currentVoidIdRef.current) {
            switchVoid(pending.spaceId);
          }
          switchChannel(pending.channelId);
        }
      } catch (e: unknown) {
        setUnlockingChannel(false);
        toast(e instanceof Error ? e.message : "Failed to unlock channel", "error");
      }
    },
    [currentVoidIdRef, fq, switchChannel, switchVoid, token, unlockModal, unlockingChannel]
  );

  return {
    lockedChannelMap,
    setLockedChannelMap,
    pendingChannelSwitchRef,
    unlockModal,
    unlockingChannel,
    maybePromptChannelUnlock,
    closeUnlockModal,
    handleUnlockChannel,
  };
}
